import { type FC } from 'react';
import { useI18nContext } from 'src/i18n/i18n-react';
import { cn } from 'src/lib/cn';
import { AriaMenu } from 'src/components/AriaMenu/AriaMenu';
import { AriaBaseButton } from 'src/components/Button/BaseButton';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { Badge } from 'src/components/Badge';
import { SquareLoaders } from 'src/components/SquareLoaders';
import { useUserAndOrganisation } from 'src/utils/useUserAndOrganisation';
import { type AdAccountResult as FacebookAdvertiserAccount } from 'src/utils/useFacebook';
import useFeatureFlag from 'src/utils/useFeatureFlag';

type TikTokAdvertiserAccount = {
  advertiserId: string;
  name: string;
};

type AdvertiserAccount = {
  advertiserId: string;
  name: string;
  profilePicURL?: string;
  role?: string;
};

type BaseProps = {
  /** The list of ad accounts to display */
  selectedAdvertiserIds: string[];
  setSelectedAdvertiserIds: (ids: string[]) => void;
  label?: string;
};

type Props = BaseProps &
  (
    | {
        platform: 'tiktok';
        advertiserAccounts: TikTokAdvertiserAccount[];
      }
    | { platform: 'facebook'; advertiserAccounts: FacebookAdvertiserAccount[] }
  );

export const InsightsAdAccountSelectorList: FC<Props> = ({
  advertiserAccounts,
  selectedAdvertiserIds,
  setSelectedAdvertiserIds,
  platform,
  label,
}) => {
  const { LL } = useI18nContext();
  const userAndOrg = useUserAndOrganisation();
  const useRoles = !!(
    useFeatureFlag('FACEBOOK_CONNECT_USE_ROLES_ENABLED') &&
    platform === 'facebook'
  );

  const existingIntegrations =
    (platform === 'facebook'
      ? userAndOrg.data?.organisation.facebookIntegrations
      : userAndOrg.data?.organisation.tikTokIntegrations) || [];

  if (userAndOrg.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <SquareLoaders className="h-10" amount={5} />
      </div>
    );
  }

  if (advertiserAccounts.length === 0) {
    return (
      <div className="italic text-purple-400">
        {LL.integrations.facebook.connectFacebook.noAdAccounts()}
      </div>
    );
  }

  let normalizedAdvertiserAccounts: AdvertiserAccount[] = [];
  switch (platform) {
    case 'tiktok': {
      normalizedAdvertiserAccounts = advertiserAccounts;
      break;
    }
    case 'facebook': {
      normalizedAdvertiserAccounts = advertiserAccounts.map((account) => ({
        advertiserId: account.accountId,
        name: account.name,
        profilePicURL: account.profilePicURL,
        role: account.role,
      }));
      break;
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-row items-center gap-2">
        {label && <h2 className="text-sm font-semibold">{label}:</h2>}
        <span className="ml-auto text-xs font-semibold opacity-50">
          {selectedAdvertiserIds.length > 0
            ? LL.selected({
                selectedCount: selectedAdvertiserIds.length,
              })
            : LL.noneSelected()}
        </span>
      </div>
      <AriaMenu.List
        items={normalizedAdvertiserAccounts}
        onSelectionChange={(set) => {
          setSelectedAdvertiserIds(Array.from(set as Set<string>));
        }}
        selectionMode="multiple"
        selectedKeys={selectedAdvertiserIds}
        className="flex max-h-96 flex-col justify-between gap-3 overflow-auto"
      >
        {({ advertiserId, name, profilePicURL, role }) => {
          const existingAccount = existingIntegrations.find(
            (integration) =>
              ('advertiserId' in integration &&
                integration.advertiserId === advertiserId) ||
              ('adAccountId' in integration &&
                integration.adAccountId === advertiserId)
          );

          const integrationStatus = existingAccount?.status;
          const exists = existingAccount != null;
          const enabled = integrationStatus === 'enabled';
          const disabled = integrationStatus === 'disabled';

          return (
            <AriaMenu.Item
              id={advertiserId}
              key={advertiserId}
              // item box
              className="font-primary h-auto shrink-0 cursor-pointer rounded-lg !border-[1.5px] border-solid border-purple-100 px-4 py-3 hover:border-purple-300 hover:bg-white focus:border-purple-300 focus:bg-white group-selected:border-purple-300"
              // checkbox
              checkboxClassName={cn(
                'size-5 rounded-md border border-solid border-purple-100 bg-white text-white',
                '!border-[1.5px] group-selected:border-purple-300 group-selected:bg-purple-800',
                'group-disabled:text-pink group-disabled:cursor-not-allowed group-disabled:border-gray-300 group-disabled:bg-gray-300'
              )}
              // text
              contentClassName="flex w-full items-center gap-2"
              showCheckbox={true}
              checkboxPosition="right"
              isDisabled={useRoles && role !== 'ADMIN'}
            >
              {/* TODO: Capture and use TikTok advertiser logo here */}
              <div className="grid size-10 place-items-center overflow-hidden rounded-md border border-solid border-purple-100 bg-purple-50 text-lg font-semibold">
                {profilePicURL ? (
                  <img
                    src={profilePicURL}
                    alt={`${name} profile`}
                    className="size-full object-cover"
                  />
                ) : (
                  <span>{name.charAt(0)}</span>
                )}
              </div>
              <div className="flex flex-col items-start gap-0.5">
                <span className="font-primary text-xs font-semibold">
                  {name}
                </span>
                {!exists && (
                  <Tooltip>
                    <AriaBaseButton className="p-0">
                      <Badge
                        color="light"
                        variant="pill"
                        className="border-none"
                      >
                        {LL.insights.accounts.accountSelect.status.notAdded.ctaText()}
                      </Badge>
                    </AriaBaseButton>
                    <span>
                      {LL.insights.accounts.accountSelect.status.notAdded.tooltipText()}
                      <br />
                      {LL.insights.accounts.accountSelect.status.notAdded.tooltipAction()}
                    </span>
                  </Tooltip>
                )}

                {exists && enabled && (
                  <Tooltip>
                    <AriaBaseButton className="p-0">
                      <Badge
                        color="success"
                        variant="pill"
                        className="border-none"
                      >
                        {LL.insights.accounts.accountSelect.status.connected.ctaText()}
                      </Badge>
                    </AriaBaseButton>
                    <span>
                      {LL.insights.accounts.accountSelect.status.connected.tooltipText()}
                      <br />
                      {LL.insights.accounts.accountSelect.status.connected.tooltipAction()}
                    </span>
                  </Tooltip>
                )}

                {exists && disabled && (
                  <Tooltip>
                    <AriaBaseButton className="p-0">
                      <Badge
                        color="danger"
                        variant="pill"
                        className="border-none"
                      >
                        {LL.insights.accounts.accountSelect.status.reConnectionRequired.ctaText()}
                      </Badge>
                    </AriaBaseButton>
                    <span>
                      {LL.insights.accounts.accountSelect.status.reConnectionRequired.tooltipText()}
                      <br />
                      {LL.insights.accounts.accountSelect.status.reConnectionRequired.tooltipAction()}
                    </span>
                  </Tooltip>
                )}

                {useRoles && role !== 'ADMIN' && (
                  <Tooltip>
                    <AriaBaseButton className="p-0">
                      <Badge
                        color="warning"
                        variant="pill"
                        className="border-none"
                      >
                        {LL.insights.accounts.accountSelect.status.noAdminAccess.ctaText()}
                      </Badge>
                    </AriaBaseButton>
                    <span>
                      {LL.insights.accounts.accountSelect.status.noAdminAccess.tooltipText()}
                      <br />
                      {LL.insights.accounts.accountSelect.status.noAdminAccess.tooltipAction()}
                    </span>
                  </Tooltip>
                )}
              </div>
            </AriaMenu.Item>
          );
        }}
      </AriaMenu.List>
    </div>
  );
};
